import {
  FC, useMemo, useRef, useState,
} from 'react';
import './housingApplicationsDetails.scss';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { HomeApplicationStatus } from '../../../interfaces/homeApplication';
import { formatCash, getClassStyle } from '../../../utils/tableHelpers';
import StyledButton from '../../../components/StyledButton';
import Buttons from '../../../components/Buttons';
import Dropdown from '../../../components/Dropdown';
import RefundIcon from '../../../svgs/RefundIcon';
import { useRolesAndPermissionGuard } from '../../../components/RolesAndPermissionGuard';
import HouseApplicationsDetailsMonitorActions, { IHouseApplicationsDetailsMonitor } from './HouseApplicationsDetailsMonitorActions';
import { handleConvertStatus } from '../../../utils/helpers';

type Props = {
  payload: any;
  refetchData: () => void;
}

const refundedStatuses: Array<{label: string; value: HomeApplicationStatus}> = [
  { label: 'Awaiting Payment', value: 'awaiting_payment' },
  { label: 'Pending', value: 'submitted' },
  { label: 'Refunded', value: 'refunded' },
];

const statuses: Array<{label: string; value: HomeApplicationStatus}> = [
  { label: 'Awaiting Payment', value: 'awaiting_payment' },
  { label: 'Pending', value: 'submitted' },
  { label: 'Processing', value: 'processing' },
  { label: 'Approved', value: 'approved' },
  { label: 'Completed', value: 'completed' },
];

const HouseApplicationsDetailsMonitor: FC<Props> = ({ payload, refetchData }) => {
  const aptInfos = [
    { name: 'Apartment type', value: payload.apartmentType },
    { name: 'City', value: payload.city },
    { name: 'Province', value: payload.province },
    { name: 'Monthly rent budget', value: formatCash(payload.monthlyBudget, 'C$') },
    { name: 'Monthly income', value: formatCash(payload.monthlyIncome, 'C$') },
    { name: 'Additional information', value: payload.additionalInformation },
  ];

  const paymentInfos = [
    { name: 'Payment ID', value: payload.meta?.paymentId },
    { name: 'Date generated', value: moment(new Date(payload.meta.dateGenerated)).format('DD MMM YYYY, hh:mma') },
    { name: 'Service cost', value: formatCash(payload.meta.serviceCost, 'C$') },
    { name: 'Initial deposit', value: formatCash(payload.meta.initialDepositPaid, 'C$') },
    { name: 'Final deposit', value: formatCash(payload.meta.finalDepositPaid, 'C$') },
  ];

  const modalRef = useRef<IHouseApplicationsDetailsMonitor>(null);
  const { id } = useParams<{id: string}>();
  const hasPermission = useRolesAndPermissionGuard('can-update-apartment-search-status');

  const statusTimeline = payload.status === 'refunded' ? refundedStatuses : statuses;
  const currentIndex = statusTimeline.findIndex((status) => status.value === payload?.status);
  const disabled = (['refunded', 'approved'] as Array<HomeApplicationStatus>).includes(payload.status);

  const getStatus = (index: number) => {
    if (index <= currentIndex - 1 || currentIndex === index) {
      return 'done';
    } if (index - 1 === currentIndex) {
      return 'pending';
    }
    return '';
    // if (index <= currentIndex - 1 || (index + 1 === statuses.length && currentIndex === index)) {
    //   return 'done';
    // } if (index === currentIndex) {
    //   return 'pending';
    // }
    // return '';
  };

  const handleDropdown = async (status: 'processing' | 'approved' | 'refunded') => {
    modalRef.current.open(status);
    // try {
    //   const { data } = await updateAdminApartment({
    //     variables: {
    //       adminUpdateApartmentSearchStatusId: id,
    //       status,
    //     },
    //   });
    //   refetchData();
    //   if (data?.adminUpdateApartmentSearchStatus?.status === 200) {
    //     Toast.success(`You have successfully updated Application status to ${status}`);
    //     return;
    //   }
    //   Toast.error('Couldn\'t update Application Status');
    // } catch (error) {
    //   Toast.error('Couldn\'t update Application Status');
    // }
  };

  return (
    <div className="house_applications_details_monitor">
      <div className="timeline-card-content">
        <div className="timelines">
          {statusTimeline.map((status, index) => (
            <div data-value={getStatus(index)} key={status.value} className="timeline">
              <div className="body">
                <p className="timeline__title">{status.label}</p>
                <span className="timeline__subtitle">
                  ****
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="application_info">
        <div className="action_toolbar">
          <div>
            <p className="action_toolbar__title">{payload?.id?.split('-')[0]}</p>
            <div className="action_toolbar__status">
              <p className="action_toolbar__subtitle">Application</p>
              <div className="status-styles">
                <div data-value={payload.status} className={[getClassStyle({ status: payload.status }, 'status')].join(' ')}>
                  {handleConvertStatus(payload.status)?.split('_')?.join(' ')}
                </div>
              </div>
            </div>
          </div>
          {hasPermission && (
            <div className="action_toolbar__buttons">
              <StyledButton
                name="Refund"
                disabled={disabled}
                backgroundColor="#F042481F"
                color="#F04248"
                border="1px solid transparent"
                width="125px"
                height="40px"
                fontSize="16px"
                borderRadius="8px"
                onClick={() => handleDropdown('refunded')}
                icon={<RefundIcon />}
              />
              <UpdateStatusDropDown
                handleDropdown={handleDropdown}
                disabled={disabled}
                status={payload?.status}
              />
            </div>
          )}
        </div>
        <div className="application_info__apt">
          <h3>Apartment Information</h3>
          <ul>
            {aptInfos.map((aptInfo) => (
              <li key={aptInfo.name}>
                <p>{aptInfo.name}</p>
                <p>{aptInfo.value}</p>
              </li>
            ))}
          </ul>
          <h3>Payment Details</h3>
          <ul>
            {paymentInfos.map((aptInfo) => (
              <li key={aptInfo.name}>
                <p>{aptInfo.name}</p>
                <p>{aptInfo.value}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <HouseApplicationsDetailsMonitorActions id={id} ref={modalRef} onSuccess={refetchData} />
    </div>
  );
};

const DropIcon = () => (
  <div className="toolbar_status__button_icon">
    <svg width="24" height="24" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M1.44064 4.06564C1.6115 3.89479 1.8885 3.89479 2.05936 4.06564L7 9.00628L11.9406 4.06564C12.1115 3.89479 12.3885 3.89479 12.5594 4.06564C12.7302 4.2365 12.7302 4.5135 12.5594 4.68436L7.30936 9.93436C7.1385 10.1052 6.8615 10.1052 6.69064 9.93436L1.44064 4.68436C1.26979 4.5135 1.26979 4.2365 1.44064 4.06564Z" fill="#FFFFFF" />
    </svg>
  </div>
);

type UpdateStatusDropDownProps = {
  handleDropdown: (item) => void;
  disabled?: boolean;
  loading?: boolean;
  status: string
};

const UpdateStatusDropDown: FC<UpdateStatusDropDownProps> = (
  {
    handleDropdown, loading, disabled, status,
  },
) => {
  const [visible, setVisible] = useState(false);
  const currentIndex = statuses.findIndex((stat) => stat.value === status);

  const onPress = (item) => {
    handleDropdown(item.value);
    setVisible(false);
  };

  const options = useMemo(() => {
    const tempOptions = [];
    if (currentIndex < 2) {
      tempOptions.push({ label: 'Change to Processing', value: 'processing', disabled: currentIndex >= 2 });
    }
    if (currentIndex >= 2) {
      tempOptions.push({ label: 'Change to Approved', value: 'approved', disabled: status === 'approved' || status === 'completed' });
    }
    return tempOptions;
  }, [currentIndex, status]);

  return (
    <div className="relative">
      <Buttons
        type="button"
        className="btn-sm bg-primary text-white toolbar_status__button"
        name={loading ? 'Loading...' : 'Update Status'}
        disabled={disabled || loading}
        EndIcon={DropIcon}
        onClick={() => setVisible((oldValue) => !oldValue)}
      />
      {visible && (
      <Dropdown
        onClick={onPress}
        className="toolbar__dropdown"
        options={options}
      />
      )}
    </div>
  );
};

export default HouseApplicationsDetailsMonitor;
